import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import Loader from './components/Loader/Loader';
import AdminLayout from './layouts/AdminLayout';

import GuestGuard from './components/Auth/GuestGuard';
import AuthGuard from './components/Auth/AuthGuard';
import AuthorizedGuard from './components/Guards/AuthorizedGuard';

import { BASE_URL, UsersRolesAttributes } from './config/constant';

// userAccount hold the data of the current logged in User
export const renderRoutes = (routes = []) => (
    <Suspense fallback={<Loader />}>
        <Switch>
            {routes.map((route, i) => {
                const Guard = route.guard || Fragment;
                const Layout = route.layout || Fragment;
                const Component = route.component;

                return (
                    <Route
                        key={i}
                        path={route.path}
                        exact={route.exact}
                        alpha="true"
                        render={(props) => (
                            <Guard>
                                <Layout>
                                    {route.routes ? renderRoutes(route.routes) : <Component {...props} className="scrollbar-container" />}
                                </Layout>
                            </Guard>
                        )}
                    />
                );
            })}
        </Switch>
    </Suspense>
);

const routes = [
    {
        exact: true,
        guard: GuestGuard,
        path: '/auth/signin',
        component: lazy(() => import('./views/auth/signin/SignIn'))
    },
    {
        exact: true,
        guard: GuestGuard,
        path: '/auth/signup',
        component: lazy(() => import('./views/auth/signup/SignUp'))
    },

    {
        path: '*',
        layout: AdminLayout,
        routes: [
            {
                exact: true,
                guard: AuthGuard,
                path: '/dashboard',
                component: lazy(() => import('./views/dashboard'))
            },
            {
                exact: true,
                guard: AuthGuard,
                path: '/results',
                component: lazy(() => import('./views/results'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.CONSULT_STATS, UsersRolesAttributes.CONSULT_DETAILED_STATS]
                    }),
                path: '/statistics',
                component: lazy(() => import('./views/statistics/pages/GeneralStats'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.CONSULT_STATS, UsersRolesAttributes.CONSULT_DETAILED_STATS]
                    }),
                path: '/statistics/prescriptions',
                component: lazy(() => import('./views/statistics/pages/PrescriptionsStats'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_DETAILED_STATS] }),
                path: '/statistics/insurances',
                component: lazy(() => import('./views/statistics/pages/AssurancesStats'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_DETAILED_STATS] }),
                path: '/statistics/antibiogrammes-fongigrammes',
                component: lazy(() => import('./views/statistics/pages/AssurancesStats'))
            },
            {
                exact: true,
                guard: AuthGuard,
                path: '/users/:uuid/profile',
                component: lazy(() => import('./views/profile'))
            },
            /**---------------------USERS----------------------------- */
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ADD_PATIENT_OR_PRESCRIPBER,
                            UsersRolesAttributes.EDIT_PATIENT_OR_PRESCRIPBER,
                            UsersRolesAttributes.DELETE_PATIENT,
                            UsersRolesAttributes.BILL_AND_PRESCRIPTION_MANAGER,
                            UsersRolesAttributes.DELETE_BILL,
                            UsersRolesAttributes.EDIT_USER_PASSWORD
                        ]
                    }),
                path: '/users/patients',
                component: lazy(() => import('./views/patients/pages/UsersList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_PACK_SUBSCRIBERS,
                            UsersRolesAttributes.CONSULT_PACK_SUBSCRIBERS_CALENDAR
                        ]
                    }),
                path: '/users/pack-subscribers',
                component: lazy(() => import('./views/patients/pages/PackSubscribersList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_UNPAIDS,
                        ]
                    }),
                path: '/users/patients-solvabilities',
                component: lazy(() => import('./views/patients/pages/PatientSolvabilitiesList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.BILL_AND_PRESCRIPTION_MANAGER] }),
                path: '/users/patients/:uuid',
                component: lazy(() => import('./views/patients/pages/UsersFolder'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.BILL_AND_PRESCRIPTION_MANAGER,
                            UsersRolesAttributes.DELETE_BILL,
                            UsersRolesAttributes.DELETE_PRESCRIPTION
                        ]
                    }),
                path: '/users/patients/:uuid/new-prescription',
                component: lazy(() => import('./views/patients/pages/UsersPrescriptionAndBillsPage'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.BILL_AND_PRESCRIPTION_MANAGER] }),
                path: '/users/patients/:uuid/:prescriptionuuid',
                component: lazy(() => import('./views/patients/pages/UsersPrescriptionAndBillsPage'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ADD_STAFF_MEMBER,
                            UsersRolesAttributes.EDIT_STAFF_MEMBER,
                            UsersRolesAttributes.DELETE_STAFF_MEMBER
                        ]
                    }),
                path: '/users/staff',
                component: lazy(() => import('./views/patients/pages/UsersList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ADD_PATIENT_OR_PRESCRIPBER,
                            UsersRolesAttributes.EDIT_PATIENT_OR_PRESCRIPBER,
                            UsersRolesAttributes.DELETE_PRESCRIBER
                        ]
                    }),
                path: '/users/prescribers',
                component: lazy(() => import('./views/patients/pages/UsersList'))
            },

            /**----------------- PHARMACY -------------------- */
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.INITIATE_PHARMACY_STOCK_MOVEMENT,
                            UsersRolesAttributes.CONSULT_PHARMACY_STOCK_STATE
                        ]
                    }),
                path: '/pharmacy/stock-state',
                component: lazy(() => import('./views/pharmacy/pages/PharmacyStockState'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.PAY_PHARMACY_BILL, UsersRolesAttributes.CREATE_PHARMACY_BILL]
                    }),
                path: '/pharmacy/medicine-selling',
                component: lazy(() => import('./views/pharmacy/pages/Billing'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.PAY_PHARMACY_BILL, UsersRolesAttributes.CREATE_PHARMACY_BILL]
                    }),
                path: '/pharmacy/medicine-selling-for-hospitalized',
                component: lazy(() => import('./views/pharmacy/pages/Billing'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_PHARMACY_STATS] }),
                path: '/pharmacy/medicines-kits',
                component: lazy(() => import('./views/pharmacy/pages/MedicinesKits'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_PHARMACY_CAISSE_GENERALE_STATS,
                            UsersRolesAttributes.CONSULT_PHARMACY_CAISSE_PERSONNELLE_STATS
                        ]
                    }),
                path: '/pharmacy/payment-reports',
                component: lazy(() => import('./views/pharmacy/pages/ReportsAgents'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_PHARMACY_STATS] }),
                path: '/pharmacy/reports-and-stats',
                component: lazy(() => import('./views/pharmacy/pages/Reports'))
            },
            // {
            //     exact: true,
            //     guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.RECEIVE_PHARMACY_MEDICINES] }),
            //     path: '/pharmacy/medicine-receptions',
            //     component: lazy(() => import('./views/pharmacy/pages/ReceptionManagement'))
            // },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.COMMAND_PHARMACY_MEDICINES,
                            UsersRolesAttributes.RECEIVE_PHARMACY_MEDICINES,
                            UsersRolesAttributes.DELETE_COMMAND_PHARMACY_MEDICINES
                        ]
                    }),
                path: '/pharmacy/medicine-commands',
                component: lazy(() => import('./views/pharmacy/pages/OrderManagement'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.PROCESS_UNORDERED_DELIVERIES]
                    }),
                path: '/pharmacy/unordered-deliveries',
                component: lazy(() => import('./views/pharmacy/pages/UnorderedDeliveries'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.INITIATE_PHARMACY_STOCK_MOVEMENT,
                            UsersRolesAttributes.CONSULT_PHARMACY_STOCK_STATE
                        ]
                    }),
                path: '/pharmacy/stock-outs',
                component: lazy(() => import('./views/pharmacy/pages/StockOuts'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_MEDCINES, UsersRolesAttributes.DELETE_MEDCINES]
                    }),
                path: '/pharmacy/medicines',
                component: lazy(() => import('./views/pharmacy/pages/MedicineManagement'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_MEDCINES, UsersRolesAttributes.DELETE_MEDCINES]
                    }),
                path: '/pharmacy/suppliers',
                component: lazy(() => import('./views/pharmacy/pages/SupplierManagement'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.IMPORT_EXPORT_PHARMACY_STOCK]
                    }),
                path: '/pharmacy/import-export',
                component: lazy(() => import('./views/pharmacy/pages/ImportExportStock'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_PHARMACY_SUPPLIERS_BILLS] }),
                path: '/pharmacy/suppliers-payments',
                component: lazy(() => import('./views/pharmacy/pages/SuppliersPayments'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_PHARMACY_MEDICINE_HISTORIES] }),
                path: '/pharmacy/medicine-histories',
                component: lazy(() => import('./views/pharmacy/pages/PharmacyMedicineHistories'))
            },

            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.INITIATE_PHARMACY_STOCK_MOVEMENT,
                            UsersRolesAttributes.CONSULT_PHARMACY_STOCK_STATE
                        ]
                    }),
                path: '/pharmacy/stock-returns',
                component: lazy(() => import('./views/pharmacy/pages/StockReturns'))
            },

            /**----------------- GESTION DES LITS - BED MANAGEMENT -------------------- */
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_HOSPITALIZED_PATIENTS] }),
                path: '/bed-management/hospitalized-patients',
                component: lazy(() => import('./views/bed-management/pages/HospitalizedPatientList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.SAMPLE_PATIENTS,
                            UsersRolesAttributes.CONSULT_BED_OCCUPATION,
                            UsersRolesAttributes.ADD_HOSPITALIZED_PATIENT
                        ]
                    }),
                path: '/bed-management/status',
                component: lazy(() => import('./views/bed-management/pages/BedStatus'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_BIRTH, UsersRolesAttributes.CONSULT_BIRTHS_LIST]
                    }),
                path: '/bed-management/births',
                component: lazy(() => import('./views/bed-management/pages/BirthsList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_DEATH, UsersRolesAttributes.CONSULT_DEATHS_LIST]
                    }),
                path: '/bed-management/deaths',
                component: lazy(() => import('./views/bed-management/pages/DeathsList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ADD_OR_EDIT_PARTNERS,
                            UsersRolesAttributes.DELETE_DEPARTMENT,
                            UsersRolesAttributes.ADD_OR_EDIT_BED_ROOM,
                            UsersRolesAttributes.DELETE_BED_ROOM
                        ]
                    }),
                path: '/bed-management/configs',
                component: lazy(() => import('./views/bed-management/pages/BedConfigs'))
            },

            /**----------------- LABORATOIRE -------------------- */
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.SAMPLE_PATIENTS] }),
                path: '/technique/samplings',
                component: lazy(() => import('./views/technique/sampling/pages/AutomaticSampling'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_BENCH_BOOK] }),
                path: '/technique/bench-book',
                component: lazy(() => import('./views/technique/benchBook/pages/BenchBook'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.SAMPLE_PATIENTS] }),
                path: '/technique/samplings/manual',
                component: lazy(() => import('./views/technique/sampling/pages/ManualSampling'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.TREAT_SAMPLES] }),
                path: '/technique/units',
                component: lazy(() => import('./views/technique/units/pages/UnitsList'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.TREAT_SAMPLES] }),
                path: '/technique/units/treatment/:uuid',
                component: lazy(() => import('./views/technique/units/pages/Treatment'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.VALIDATE_SAMPLES] }),
                path: '/technique/validation',
                component: lazy(() => import('./views/technique/units/pages/ValidationsList'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.VALIDATE_SAMPLES] }),
                path: '/technique/units/validation/:uuid',
                component: lazy(() => import('./views/technique/units/pages/Treatment'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.EDIT_VALIDATED_PRESCRIPTIONS, UsersRolesAttributes.TREAT_SAMPLES]
                    }),
                path: '/technique/units/edit-sample/:uuid',
                component: lazy(() => import('./views/technique/units/pages/Treatment'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.PRINT_AND_WITHDRAW_RESULTS] }),
                path: '/technique/print-and-withdraw',
                component: lazy(() => import('./views/technique/print-and-withdraw/pages/PrintAndWithdraw'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_GENERAL_PRESCRIPTIONS_HISTORIES] }),
                path: '/technique/general-histories',
                component: lazy(() => import('./views/technique/units/pages/GeneralPrescriptionHistories'))
            },

            /**----------------- CAISSE -------------------- */
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_AND_PRINT_CAISSE_STATE] }),
                path: '/cash/general',
                component: lazy(() => import('./views/caisse/pages/CaisseGenerale'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.PERSONAL_CAISSE] }),
                path: '/cash/personnal',
                component: lazy(() => import('./views/caisse/pages/CaisseGenerale'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.PERSONAL_CAISSE, UsersRolesAttributes.CONSULT_AND_PRINT_CAISSE_STATE] }),
                path: '/cash/cautions',
                component: lazy(() => import('./views/caisse/pages/CautionsState'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.EDIT_CAISSE_OUTPUT,
                            UsersRolesAttributes.VALIDATE_CAISSE_OUTPUT,
                            UsersRolesAttributes.CANCEL_VALIDATED_CAISSE_OUTPUT
                        ]
                    }),
                path: '/cash/cash-outputs',
                component: lazy(() => import('./views/caisse/pages/CaisseOutputs'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.VALIDATE_CAISSE_OUTPUT] }),
                path: '/cash/cash-outputs/validation',
                component: lazy(() => import('./views/caisse/pages/CaisseOutputsValidation'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_AND_PRINT_QUOTE_PART,
                            UsersRolesAttributes.ADD_QUOTE_PART,
                            UsersRolesAttributes.DELETE_QUOTE_PART
                        ]
                    }),
                path: '/cash/quote-part',
                component: lazy(() => import('./views/caisse/pages/QuoteParts'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_AND_PRINT_QUOTE_PART,
                            UsersRolesAttributes.ADD_QUOTE_PART,
                            UsersRolesAttributes.DELETE_QUOTE_PART
                        ]
                    }),
                path: '/cash/quote-part-assurances',
                component: lazy(() => import('./views/caisse/pages/QuotePartsAssurances'))
            },
            {
                exact: true,
                // guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [] }),
                guard: AuthGuard,
                path: '/prescriber/quote-part/details',
                component: lazy(() => import('./views/caisse/pages/QuotePartsDetailsForPrescriber'))
            },
            {
                exact: true,
                // guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [] }),
                guard: AuthGuard,
                path: '/prescriber/quote-part',
                component: lazy(() => import('./views/caisse/pages/QuotePartsForPrescriber'))
            },
            {
                exact: true,
                // guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [] }),
                guard: AuthGuard,
                path: '/prescriber/prescriptions',
                component: lazy(() => import('./views/caisse/pages/PrescriptionsForPrescriber'))
            },

            /**----------------- IMAGERIE -------------------- */
             {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ACCESS_IMAGING_MODULE,
                        ]
                    }),
                path: '/imaging/patients',
                component: lazy(() => import('./views/imagerie/pages/ImagingUsersList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.ACCESS_IMAGING_MODULE] }),
                path: '/imaging/patients/:uuid',
                component: lazy(() => import('./views/imagerie/pages/ImagingUsersFolder'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.ACCESS_IMAGING_MODULE] }),
                path: '/imaging/patients/:uuid/new',
                component: lazy(() => import('./views/imagerie/pages/ImagingExamination'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.ACCESS_IMAGING_MODULE] }),
                path: '/imaging/patients/:uuid/:examinationuuid/edit',
                component: lazy(() => import('./views/imagerie/pages/ImagingExamination'))
            },

            /**----------------- CONSULTATION & HOSPITALIZATION -------------------- */
             {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_PATIENTS_LIST,
                        ]
                    }),
                path: '/consultations-hospitalizations/patients',
                component: lazy(() => import('./views/consultations/pages/ConsultationUsersList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_PATIENTS_LIST] }),
                path: '/consultations-hospitalizations/patients/:uuid',
                component: lazy(() => import('./views/consultations/pages/ConsultationsHospitalizationUsersFolder'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_PATIENTS_LIST] }),
                path: '/consultations-hospitalizations/patients/:uuid/new',
                component: lazy(() => import('./views/consultations/pages/NewConsultation'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_PATIENTS_LIST] }),
                path: '/consultations-hospitalizations/patients/:uuid/:consultationuuid/edit',
                component: lazy(() => import('./views/consultations/pages/NewConsultation'))
            },
            {
               exact: true,
               guard: (children) =>
                   AuthorizedGuard({
                       ...children,
                       authorizedRoles: [
                           UsersRolesAttributes.CONSULT_PATIENTS_RDV,
                       ]
                   }),
               path: '/consultations-hospitalizations/rendez-vous',
               component: lazy(() => import('./views/consultations/pages/RendezVousList'))
           },

            /**----------------- CPN -------------------- */
             {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_CPN_PATIENTS_LIST,
                        ]
                    }),
                path: '/cpn/patients',
                component: lazy(() => import('./views/cpn/pages/CPNUsersList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_CPN_PATIENTS_LIST] }),
                path: '/cpn/patients/:uuid',
                component: lazy(() => import('./views/cpn/pages/CPNUsersFolder'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_CPN_PATIENTS_LIST] }),
                path: '/cpn/patients/:uuid/new',
                component: lazy(() => import('./views/cpn/pages/CPNUsersFolder'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_CPN_PATIENTS_LIST] }),
                path: '/cpn/patients/:uuid/:consultationuuid/edit',
                component: lazy(() => import('./views/consultations/pages/NewConsultation'))
            },
            {
               exact: true,
               guard: (children) =>
                   AuthorizedGuard({
                       ...children,
                       authorizedRoles: [
                           UsersRolesAttributes.CONSULT_CPN_PATIENTS_RDV,
                       ]
                   }),
               path: '/cpn/rendez-vous',
               component: lazy(() => import('./views/consultations/pages/RendezVousList'))
           },

            /**-----------------ADMINISTRATION -------------------- */
            {
                exact: true,
                // guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONFIGURE_PACKS] }),
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_EXAMS] }),
                path: '/admin/packs-configs',
                component: lazy(() => import('./views/admin/packsConfigs/pages/PacksList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.CONSULT_EXAMS_LIST,
                            UsersRolesAttributes.ADD_OR_EDIT_EXAMS,
                            UsersRolesAttributes.EDIT_EXAMS_SETTINGS,
                            UsersRolesAttributes.DELETE_EXAMS
                        ]
                    }),
                path: '/admin/exams/list',
                component: lazy(() => import('./views/admin/exams/pages/ExamsList'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.EDIT_EXAMS_SETTINGS] }),
                path: '/admin/exams/exam/:uuid',
                component: lazy(() => import('./views/admin/exams/pages/ExamsSettings'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_EXAMS]
                    }),
                path: '/admin/exams/kits',
                component: lazy(() => import('./views/admin/exams/pages/KitsList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_EXAMS_TUBES, UsersRolesAttributes.DELETE_EXAMS_TUBES]
                    }),
                path: '/admin/exams/tubes',
                component: lazy(() => import('./views/admin/exams/pages/ExamsTubesList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ADD_OR_EDIT_EXAMS_TYPES,
                            UsersRolesAttributes.DELETE_EXAMS_TYPES,
                            UsersRolesAttributes.EDIT_EXAMS_TYPES_SETTINGS,
                            UsersRolesAttributes.EDIT_QUOTE_PART_PERCENTAGE
                        ]
                    }),
                path: '/admin/exams/types',
                component: lazy(() => import('./views/admin/exams/pages/ExamsTypesList'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.EDIT_EXAMS_TYPES_SETTINGS] }),
                path: '/admin/exams/types/:uuid',
                component: lazy(() => import('./views/admin/exams/pages/ExamsTypesSettings'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_RESULTS_BANK, UsersRolesAttributes.DELETE_RESULTS_BANK]
                    }),
                path: '/admin/exams/parameters-results',
                component: lazy(() => import('./views/admin/exams/pages/ExamsParametersValues'))
            },

            {
                exact: true,
                path: '/admin/profiles',
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.EDIT_USER_PROFILE] }),
                component: lazy(() => import('./views/admin/profiles/pages/profilesList/Profiles'))
            },

            // {
            //     exact: true,
            //     path: '/admin/pending-discounts',
            //     guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.VALIDATE_PENDING_DISCOUNTS] }),
            //     component: lazy(() => import('./views/admin/PendingDiscounts/PendingDiscounts'))
            // },

            {
                exact: true,
                path: '/admin/discounts',
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.VALIDATE_PENDING_DISCOUNTS] }),
                component: lazy(() => import('./views/admin/PendingDiscounts/AssignedDiscounts'))
            },

            {
                exact: true,
                path: '/admin/push-notifications',
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.SEND_PUSH_NOTIFICATIONS] }),
                component: lazy(() => import('./views/admin/PushNotifications/PushNotifications'))
            },

            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_LABS, UsersRolesAttributes.DELETE_LABS]
                    }),
                path: '/admin/configs/laboratories',
                component: lazy(() => import('./views/admin/configs/laboratories/pages/LaboratoriesList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [UsersRolesAttributes.ADD_OR_EDIT_PARTNERS, UsersRolesAttributes.DELETE_PARTNERS]
                    }),
                path: '/admin/configs/partners',
                component: lazy(() => import('./views/admin/configs/partners/pages/PartnersList'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ADD_CAISSE_OUTPUT_ORDONNATOR,
                            UsersRolesAttributes.EDIT_CAISSE_OUTPUT_ORDONNATOR,
                            UsersRolesAttributes.DELETE_CAISSE_OUTPUT_ORDONNATOR,
                            UsersRolesAttributes.ADD_CAISSE_OUTPUT_DOMAIN,
                            UsersRolesAttributes.EDIT_CAISSE_OUTPUT_DOMAIN,
                            UsersRolesAttributes.DELETE_CAISSE_OUTPUT_DOMAIN
                        ]
                    }),
                path: '/admin/configs/caisse',
                component: lazy(() => import('./views/admin/configs/caisse/pages/CaisseConfigs'))
            },
            {
                exact: true,
                guard: (children) =>
                    AuthorizedGuard({
                        ...children,
                        authorizedRoles: [
                            UsersRolesAttributes.ADD_CAISSE_OUTPUT_ORDONNATOR,
                        ]
                    }),
                path: '/admin/configs/hospitalisations-settings',
                component: lazy(() => import('./views/admin/configs/hospitalisation-settings/pages/HospitalizationConfigs'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.CONSULT_HISTORIES] }),
                path: '/admin/histories',
                component: lazy(() => import('./views/admin/histories/Histories'))
            },
            {
                exact: true,
                guard: (children) => AuthorizedGuard({ ...children, authorizedRoles: [UsersRolesAttributes.HANDLE_TAXES_MANAGEMENT] }),
                path: '/admin/taxes',
                component: lazy(() => import('./views/admin/taxes/TaxesManagement'))
            },

            {
                path: '*',
                exact: true,
                component: () => <Redirect to={BASE_URL} />
            }
        ]
    }
];

export default routes;
